import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { PageHeader } from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "../components/ui/dropdown-menu";
import { Plus, MoreHorizontal, Loader2, Pencil, Trash2, Mail, Phone, Users as UsersIcon, Package } from "lucide-react";
import { PrintButton, PrintHeader } from "../components/Print";

function VendorDialog({ open, onOpenChange, vendor, onSuccess }) {
  const initial = { name: "", contact_name: "", email: "", phone: "", address: "", notes: "" };
  const [form, setForm] = useState(initial);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) setForm(vendor ? { ...initial, ...vendor } : initial);
  }, [open, vendor]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Vendor name is required");
    setBusy(true);
    try {
      if (vendor) {
        await api.patch(`/vendors/${vendor.id}`, form);
        toast.success("Vendor updated");
      } else {
        await api.post("/vendors", form);
        toast.success(`Added ${form.name}`);
      }
      onOpenChange(false);
      onSuccess?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-md max-w-lg" data-testid="vendor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-black tracking-tight">
            {vendor ? "Edit vendor" : "New vendor"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Vendor name</Label>
            <Input
              required value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              data-testid="vendor-name-input"
              className="rounded-sm mt-1.5" placeholder="Acme Cleaning Supply"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Contact name</Label>
              <Input value={form.contact_name} onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))} data-testid="vendor-contact-input" className="rounded-sm mt-1.5" placeholder="Alice Turner" />
            </div>
            <div>
              <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Phone</Label>
              <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} data-testid="vendor-phone-input" className="rounded-sm mt-1.5" placeholder="555-1234" />
            </div>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Email</Label>
            <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} data-testid="vendor-email-input" className="rounded-sm mt-1.5" placeholder="orders@acme.com" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Address</Label>
            <Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} data-testid="vendor-address-input" className="rounded-sm mt-1.5" placeholder="123 Industrial Way" />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-widest text-slate-500">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} data-testid="vendor-notes-input" className="rounded-sm mt-1.5" />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={busy} data-testid="vendor-submit-btn" className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm">
              {busy && <Loader2 size={16} className="animate-spin mr-1.5" />}
              {vendor ? "Save changes" : "Add vendor"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function VendorsPage() {
  const [vendors, setVendors] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [items, setItems] = useState([]);
  const { canDelete } = useAuth();

  const load = async () => {
    const [{ data: v }, { data: i }] = await Promise.all([
      api.get("/vendors"),
      api.get("/inventory/items"),
    ]);
    setVendors(v);
    setItems(i);
  };
  useEffect(() => { load(); }, []);

  const linkedCount = (vid) => items.filter((i) => i.vendor_id === vid).length;

  const doDelete = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"? Linked items will be unlinked.`)) return;
    try {
      await api.delete(`/vendors/${v.id}`);
      toast.success("Vendor removed");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Supply chain"
        title="Vendors"
        description="Every supplier you work with, ready for reorders and purchase orders."
        testId="vendors-header"
        actions={
          <div className="flex items-center gap-2 no-print">
            <PrintButton testId="vendors-print-btn" />
            <Button
              onClick={() => { setEditing(null); setDialogOpen(true); }}
              data-testid="new-vendor-btn"
              className="bg-blue-800 hover:bg-blue-900 text-white rounded-sm"
            >
              <Plus size={16} className="mr-1.5" /> New Vendor
            </Button>
          </div>
        }
      />

      <div className="px-4 md:px-8 pt-6 print-container">
        <PrintHeader title="Vendor Directory" subtitle={`${vendors.length} vendor${vendors.length === 1 ? "" : "s"}`} />
        {vendors.length === 0 ? (
          <div className="border border-dashed border-slate-300 bg-white rounded-md p-12 text-center">
            <UsersIcon size={40} className="mx-auto text-slate-400" />
            <div className="mt-3 font-display text-xl font-bold text-slate-900">No vendors yet</div>
            <p className="mt-1 text-sm text-slate-600 max-w-md mx-auto">
              Add your first supplier so items can be linked and purchase orders start flowing.
            </p>
            <Button
              onClick={() => { setEditing(null); setDialogOpen(true); }}
              data-testid="new-vendor-empty-btn"
              className="mt-4 bg-blue-800 hover:bg-blue-900 text-white rounded-sm"
            >
              <Plus size={16} className="mr-1.5" /> Add first vendor
            </Button>
          </div>
        ) : (
          <div className="border border-slate-200 rounded-md overflow-x-auto bg-white shadow-sm">
            <table className="w-full text-sm min-w-[760px]" data-testid="vendors-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr className="text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                  <th className="p-4">Vendor</th>
                  <th className="p-4">Contact</th>
                  <th className="p-4">Email</th>
                  <th className="p-4">Phone</th>
                  <th className="p-4 text-right">Linked Items</th>
                  <th className="p-4 w-16 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {vendors.map((v) => (
                  <tr key={v.id} className="border-b border-slate-200 last:border-b-0 hover:bg-slate-50 transition-colors" data-testid={`vendor-row-${v.id}`}>
                    <td className="p-4 font-semibold text-slate-900">
                      {v.name}
                      {v.address && <div className="mt-0.5 font-normal text-xs text-slate-500">{v.address}</div>}
                    </td>
                    <td className="p-4 text-slate-700">{v.contact_name || <span className="text-slate-400">—</span>}</td>
                    <td className="p-4 text-slate-700 font-mono text-xs">
                      {v.email ? <a href={`mailto:${v.email}`} className="text-blue-800 hover:underline inline-flex items-center gap-1"><Mail size={12} /> {v.email}</a> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="p-4 text-slate-700 font-mono text-xs">
                      {v.phone ? <span className="inline-flex items-center gap-1"><Phone size={12} /> {v.phone}</span> : <span className="text-slate-400">—</span>}
                    </td>
                    <td className="p-4 text-right">
                      <span className="inline-flex items-center gap-1 text-xs font-mono font-semibold text-slate-700">
                        <Package size={14} /> {linkedCount(v.id)}
                      </span>
                    </td>
                    <td className="p-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label="Vendor actions" data-testid={`vendor-actions-${v.id}`} className="h-8 w-8 rounded-sm hover:bg-slate-100">
                            <MoreHorizontal size={18} />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48 rounded-sm">
                          <DropdownMenuLabel className="text-xs uppercase tracking-widest text-slate-500">Actions</DropdownMenuLabel>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => { setEditing(v); setDialogOpen(true); }} data-testid={`vendor-edit-${v.id}`}>
                            <Pencil size={14} className="mr-2" /> Edit
                          </DropdownMenuItem>
                          {canDelete && (
                            <DropdownMenuItem onClick={() => doDelete(v)} className="text-red-600 focus:text-red-700 focus:bg-red-50" data-testid={`vendor-delete-${v.id}`}>
                              <Trash2 size={14} className="mr-2" /> Delete
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <VendorDialog open={dialogOpen} onOpenChange={setDialogOpen} vendor={editing} onSuccess={load} />
    </div>
  );
}
